import Navigation from "../components/Navigation";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Landmark,
  Gauge,
  Calculator,
  ArrowUpRight,
  ArrowRight,
} from "lucide-react";

// Tool registry — copy lives in the i18n locale files under `toolsPage.items.<id>`,
// non-translatable data (target URL, status, icon) stays here.
const TOOLS = [
  {
    id: "foerdermittel",
    icon: Landmark,
    url: "https://foerdermittel.daaniyalkhan.com",
    status: "live",
  },
  {
    id: "valuate",
    icon: Gauge,
    url: "https://valuate.daaniyalkhan.com",
    status: "live",
  },
  {
    id: "taxleak",
    icon: Calculator,
    url: "https://taxleak.daaniyalkhan.com",
    status: "live",
  },
  // 28.08.2026: Die zwei WIP-Kacheln ("salesPsychology", "more") sind raus.
  // Sie standen seit dem 06.06. unveraendert auf "in Arbeit" — bei einem
  // Digitalisierungsangebot liest sich das als Stillstand, nicht als Vorschau.
  // Die Render-Zweige fuer status "wip" bleiben bestehen, falls je wieder ein
  // Tool angekuendigt werden soll; die i18n-Keys wurden entfernt.
];

const StatusBadge = ({ status, t }) => {
  const live = status === "live";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${
        live ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          live ? "bg-green-500" : "bg-amber-500"
        }`}
        aria-hidden="true"
      />
      {live ? t("toolsPage.badgeLive") : t("toolsPage.badgeWip")}
    </span>
  );
};

const CardBody = ({ tool, t }) => {
  const Icon = tool.icon;
  const live = tool.status === "live";
  return (
    <>
      <div className="flex items-start justify-between mb-6">
        <div className="w-14 h-14 rounded-xl bg-[#C5A66A]/10 flex items-center justify-center group-hover:bg-[#C5A66A] transition-colors duration-300">
          <Icon
            size={28}
            className="text-[#C5A66A] group-hover:text-white transition-colors duration-300"
            aria-hidden="true"
          />
        </div>
        <StatusBadge status={tool.status} t={t} />
      </div>

      <h2 className="font-playfair text-2xl font-bold text-[#0F1B2D] mb-2 group-hover:text-[#C5A66A] transition-colors">
        {t(`toolsPage.items.${tool.id}.name`)}
      </h2>
      <p className="text-[#C5A66A] font-medium mb-4">
        {t(`toolsPage.items.${tool.id}.tagline`)}
      </p>
      <p className="text-gray-600 leading-relaxed flex-grow mb-6">
        {t(`toolsPage.items.${tool.id}.description`)}
      </p>

      <div className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-6">
        {t(`toolsPage.items.${tool.id}.audience`)}
      </div>

      <div
        className={`flex items-center font-semibold transition-colors ${
          live
            ? "text-[#0F1B2D] group-hover:text-[#C5A66A]"
            : tool.to
            ? "text-[#0F1B2D] group-hover:text-[#C5A66A]"
            : "text-gray-400"
        }`}
      >
        {live ? (
          <>
            {t("toolsPage.openCta")}
            <ArrowUpRight
              size={18}
              className="ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
            />
          </>
        ) : tool.to ? (
          <>
            {t("toolsPage.notifyCta")}
            <ArrowRight
              size={18}
              className="ml-1 group-hover:translate-x-1 transition-transform"
            />
          </>
        ) : (
          <>{t("toolsPage.wipCta")}</>
        )}
      </div>
    </>
  );
};

const ToolCard = ({ tool, t }) => {
  const live = tool.status === "live";
  const baseLive =
    "group flex flex-col h-full bg-white rounded-2xl border border-gray-200 hover:border-[#C5A66A] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-8";
  const baseWip =
    "group flex flex-col h-full bg-[#FAFAF8] rounded-2xl border border-dashed border-gray-300 hover:border-[#C5A66A] transition-all duration-300 p-8";

  if (live) {
    return (
      <a
        href={tool.url}
        target="_blank"
        rel="noopener noreferrer"
        className={baseLive}
        aria-label={`${t(`toolsPage.items.${tool.id}.name`)} — ${t(
          "toolsPage.openCta"
        )}`}
      >
        <CardBody tool={tool} t={t} />
      </a>
    );
  }

  if (tool.to) {
    return (
      <Link to={tool.to} className={baseWip}>
        <CardBody tool={tool} t={t} />
      </Link>
    );
  }

  return (
    <div className={`${baseWip} cursor-default`}>
      <CardBody tool={tool} t={t} />
    </div>
  );
};

const Tools = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Skip link for keyboard users — WCAG 2.4.1 AA */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-[#C5A66A] focus:text-[#0F1B2D] focus:px-4 focus:py-2 focus:rounded focus:font-semibold"
      >
        Zum Hauptinhalt springen
      </a>
      <Navigation />
      <main id="main-content" className="pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-6xl">
          <header className="mb-16 max-w-3xl">
            <div className="text-sm text-[#C5A66A] uppercase tracking-wider font-semibold mb-4">
              {t("toolsPage.eyebrow")}
            </div>
            <h1 className="font-playfair text-5xl lg:text-6xl font-bold text-[#0F1B2D] mb-6 leading-tight">
              {t("toolsPage.title")}
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              {t("toolsPage.subtitle")}
            </p>
          </header>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 auto-rows-fr">
            {TOOLS.map((tool) => (
              <ToolCard key={tool.id} tool={tool} t={t} />
            ))}
          </div>

          <section className="mt-20 bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4 text-[#C5A66A]">
              {t("toolsPage.closing.title")}
            </h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              {t("toolsPage.closing.subtitle")}
            </p>
            <Link
              to="/#contact"
              className="inline-block bg-[#C5A66A] text-[#0F1B2D] px-8 py-4 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
            >
              {t("toolsPage.closing.button")}
            </Link>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Tools;
